import {
    Box,
    HStack,
    Link,
    Text,
    VStack,
} from "@chakra-ui/react";
import { QrCode } from "lucide-react";
import { useCustomTheme } from "../contexts/CustomThemeContext";

type PickDenguWhatsAppQrProps = {
    source?: string;
    campaign?: string;
    title?: string;
    description?: string;
    compact?: boolean;
};

const BACKEND_URL =
    process.env.REACT_APP_PUBLIC_API_BASE_URL || "https://api.pickdengu.mw";

const WHATSAPP_QR_VERSION = "2026-05-11-01";

const removeTrailingSlash = (value: string) => value.replace(/\/$/, "");

const buildQrUrl = (source: string, campaign: string) => {
    const baseUrl = removeTrailingSlash(BACKEND_URL);

    return `${baseUrl}/api/v1/whatsapp/qr/image?source=${encodeURIComponent(
        source,
    )}&campaign=${encodeURIComponent(campaign)}&v=${WHATSAPP_QR_VERSION}`;
};

const buildDownloadUrl = (source: string, campaign: string) => {
    const baseUrl = removeTrailingSlash(BACKEND_URL);

    return `${baseUrl}/api/v1/whatsapp/qr/download?source=${encodeURIComponent(
        source,
    )}&campaign=${encodeURIComponent(campaign)}&v=${WHATSAPP_QR_VERSION}`;
};

const buildStartUrl = (source: string, campaign: string) => {
    const baseUrl = removeTrailingSlash(BACKEND_URL);

    return `${baseUrl}/api/v1/whatsapp/qr/start?source=${encodeURIComponent(
        source,
    )}&campaign=${encodeURIComponent(campaign)}`;
};

const PickDenguWhatsAppQr = ({
    source = "website",
    campaign = "homepage",
    title = "Scan to chat",
    description = "Scan, tap Send, and PickDengu opens the menu.",
    compact = false,
}: PickDenguWhatsAppQrProps) => {
    const { theme } = useCustomTheme();

    const qrImageUrl = buildQrUrl(source, campaign);
    const startUrl = buildStartUrl(source, campaign);

    return (
        <VStack
            w={{ base: "100%", sm: compact ? "220px" : "300px" }}
            maxW={{ base: "100%", sm: compact ? "220px" : "300px" }}
            borderRadius="24px"
            position="relative"
            overflow="hidden"
        >
            <Box
                position="relative"
            >
                <Box
                    bg="white"
                    borderRadius="18px"
                    position="relative"
                    as="a"
                    href={startUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    display="block"
                >
                    <img
                        src={qrImageUrl}
                        alt={`${title} QR code`}
                        width={compact ? 148 : 210}
                        height={compact ? 148 : 210}
                        style={{
                            display: "block",
                            width: compact ? "148px" : "210px",
                            height: compact ? "148px" : "210px",
                            objectFit: "contain",
                        }}
                    />
                </Box>
            </Box>

            {!compact && (
                <Link
                    href={startUrl}
                    fontSize="10px"
                    color={theme.textMuted}
                    wordBreak="break-all"
                    target="_blank"
                    rel="noopener noreferrer"
                    opacity={0.75}
                    _hover={{ opacity: 1 }}
                    position="relative"
                >
                    <HStack justify="center" spacing={1}>
                        <QrCode size={12} />
                        <Text noOfLines={1}>Scan/Click QR to Chat</Text>
                    </HStack>
                </Link>
            )}
        </VStack>
    );
};

export default PickDenguWhatsAppQr;